import { useState, useEffect } from "react";
import { useParams, useNavigate, Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
  Calendar, Clock, Users, ArrowLeft, Loader2, 
  Share2, Copy, ExternalLink, UserCircle, Building2
} from "lucide-react";
import { toast } from "sonner";
import { safeFetchJson } from "@/utils/safeFetch";
import WhatsAppSupport from "@/components/WhatsAppSupport";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_talentmeet-app/artifacts/w1exeb5m_IMG-20260127-WA0008.jpg";

// Event type badge styles
const EVENT_TYPE_STYLES = {
  audition: { bg: "bg-purple-100", text: "text-purple-800", label: "Audition" },
  meeting: { bg: "bg-blue-100", text: "text-blue-800", label: "Meeting" },
  callback: { bg: "bg-orange-100", text: "text-orange-800", label: "Callback" },
  workshop: { bg: "bg-green-100", text: "text-green-800", label: "Workshop" },
  live_class: { bg: "bg-pink-100", text: "text-pink-800", label: "Live Class" }
};

// Default poster placeholder
const DEFAULT_POSTER = "https://images.unsplash.com/photo-1540575467063-178a50c2df87?w=800&h=400&fit=crop";

export default function PublicEventPage() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [registering, setRegistering] = useState(false);
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem("token");
    setIsLoggedIn(!!token);
  }, []);

  useEffect(() => {
    if (eventId) {
      fetchEvent();
    }
  }, [eventId]);

  const fetchEvent = async () => {
    setLoading(true);
    try {
      const parsed = await safeFetchJson(`${API_URL}/api/events/public/${eventId}`);
      if (parsed.ok) {
        setEvent(parsed.data);
      } else {
        throw new Error(parsed.data?.detail || "Event not found");
      }
    } catch (error) {
      console.error("Error fetching event:", error);
      toast.error("Event not found");
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async () => {
    if (!isLoggedIn) {
      // Save current URL and redirect to login
      toast.info("Please sign in to register for this event");
      navigate("/login", { state: { from: location } });
      return;
    }

    setRegistering(true);
    try {
      const token = localStorage.getItem("token");
      const parsed = await safeFetchJson(`${API_URL}/api/events/${eventId}/register`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!parsed.ok) {
        throw new Error(parsed.data?.detail || "Failed to register");
      }

      toast.success("Successfully registered! Check your dashboard for the meeting link.");
      fetchEvent(); // Refresh event data
    } catch (error) {
      toast.error(error.message);
    } finally {
      setRegistering(false);
    }
  };

  const handleShare = async (type) => {
    const shareUrl = window.location.href;
    const shareText = `Check out this event: ${event?.title} on TalentMeet`;

    if (type === "copy") {
      try {
        await navigator.clipboard.writeText(shareUrl);
        toast.success("Link copied to clipboard!");
      } catch (err) {
        // Fallback for browsers that don't support clipboard API
        const textArea = document.createElement("textarea");
        textArea.value = shareUrl;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand("copy");
        document.body.removeChild(textArea);
        toast.success("Link copied!");
      }
    } else if (type === "whatsapp") {
      const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(shareText + "\n" + shareUrl)}`;
      window.open(whatsappUrl, "_blank");
    } else if (type === "native" && navigator.share) {
      try {
        await navigator.share({
          title: event?.title,
          text: shareText,
          url: shareUrl
        });
      } catch (err) {
        // User cancelled or error
        console.log("Share cancelled");
      }
    }
    setShowShareMenu(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-violet-600" />
        <span className="ml-3 text-gray-600">Loading event...</span>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center px-4">
        <Calendar className="w-16 h-16 text-gray-300 mb-4" />
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Event Not Found</h2>
        <p className="text-gray-600 mb-6">This event may have been cancelled or removed.</p>
        <Button onClick={() => navigate("/events")} className="bg-violet-600 hover:bg-violet-700">
          Browse Public Events
        </Button>
      </div>
    );
  }

  const eventType = EVENT_TYPE_STYLES[event.event_type] || EVENT_TYPE_STYLES.meeting;
  const eventDate = new Date(event.start_at);
  const dateStr = eventDate.toLocaleDateString("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric"
  });
  const timeStr = eventDate.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true
  });

  // Host info
  const hostName = event.host_info?.display_name || event.host_info?.name || "Host";
  const hostPhoto = event.host_info?.profile_photo || event.host_info?.logo;
  const companyName = event.host_info?.company_name;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate(-1)}
              className="text-gray-600"
            >
              <ArrowLeft className="w-4 h-4 mr-1" />
              Back
            </Button>
            <Link to="/" className="flex items-center gap-2">
              <img src={LOGO_URL} alt="TalentMeet" className="h-8 w-8 rounded-lg" />
              <span className="font-bold text-xl text-gray-900 hidden sm:block">TalentMeet</span>
            </Link>
          </div>
          <div className="flex items-center gap-2">
            {!isLoggedIn && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate("/login")}
                >
                  Sign In
                </Button>
                <Button
                  size="sm"
                  className="bg-violet-600 hover:bg-violet-700"
                  onClick={() => navigate("/register")}
                >
                  Join Free
                </Button>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-6">
        {/* Event Poster */}
        <div className="relative rounded-2xl overflow-hidden mb-6 shadow-lg">
          <img
            src={event.poster_url || DEFAULT_POSTER}
            alt={event.title}
            className="w-full h-48 sm:h-64 md:h-80 object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
          <div className="absolute bottom-4 left-4 right-4">
            <Badge className={`${eventType.bg} ${eventType.text} mb-2`}>
              {eventType.label}
            </Badge>
            {event.visibility_scope === "public" && (
              <Badge variant="outline" className="ml-2 bg-white/90 text-gray-800">
                Public Event
              </Badge>
            )}
          </div>
        </div>

        {/* Event Details Card */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 mb-6">
          {/* Host Info */}
          <div className="flex items-center gap-3 mb-4 pb-4 border-b border-gray-100">
            {hostPhoto ? (
              <img
                src={hostPhoto}
                alt={hostName}
                className="w-12 h-12 rounded-full object-cover border-2 border-violet-200"
              />
            ) : (
              <div className="w-12 h-12 rounded-full bg-violet-100 flex items-center justify-center">
                {event.host_info?.profile_type === "enterprise" ? (
                  <Building2 className="w-6 h-6 text-violet-600" />
                ) : (
                  <UserCircle className="w-6 h-6 text-violet-600" />
                )}
              </div>
            )}
            <div>
              <p className="font-semibold text-gray-900">{hostName}</p>
              {companyName && companyName !== hostName && (
                <p className="text-sm text-gray-500">{companyName}</p>
              )}
              <p className="text-xs text-gray-400 capitalize">{event.host_info?.profile_type || "Host"}</p>
            </div>
          </div>

          {/* Event Title */}
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">
            {event.title}
          </h1>

          {/* Description */}
          {event.description && (
            <p className="text-gray-600 mb-6 whitespace-pre-line">
              {event.description}
            </p>
          )}

          {/* Date, Time, Duration */}
          <div className="grid sm:grid-cols-2 gap-4 mb-6">
            <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-xl">
              <Calendar className="w-5 h-5 text-violet-600 mt-0.5" />
              <div>
                <p className="text-sm text-gray-500">Date</p>
                <p className="font-medium text-gray-900">{dateStr}</p>
              </div>
            </div>
            <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-xl">
              <Clock className="w-5 h-5 text-violet-600 mt-0.5" />
              <div>
                <p className="text-sm text-gray-500">Time & Duration</p>
                <p className="font-medium text-gray-900">{timeStr} ({event.duration_minutes} min)</p>
              </div>
            </div>
          </div>

          {/* Facilitators */}
          {event.facilitators && event.facilitators.length > 0 && (
            <div className="mb-6">
              <p className="text-sm text-gray-500 mb-2">Facilitators</p>
              <div className="flex flex-wrap gap-2">
                {event.facilitators.map(fac => (
                  <span
                    key={fac.trainer_user_id}
                    className="inline-flex items-center gap-2 px-3 py-1.5 bg-blue-50 text-blue-700 rounded-full text-sm"
                  >
                    <UserCircle className="w-4 h-4" />
                    {fac.trainer_info?.display_name || "Trainer"}
                    {fac.role_label === "lead" && <Badge className="text-xs">Lead</Badge>}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Participant Count */}
          {event.participant_count > 0 && (
            <div className="flex items-center gap-2 text-sm text-gray-600 mb-6">
              <Users className="w-4 h-4" />
              <span>{event.participant_count} registered</span>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-3">
            <Button
              size="lg"
              className="flex-1 bg-violet-600 hover:bg-violet-700"
              onClick={handleRegister}
              disabled={registering}
              data-testid="register-event-btn"
            >
              {registering ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Registering...
                </>
              ) : (
                <>
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Register to Join
                </>
              )}
            </Button>

            {/* Share Button */}
            <div className="relative">
              <Button
                variant="outline"
                size="lg"
                onClick={() => setShowShareMenu(!showShareMenu)}
                className="w-full sm:w-auto"
                data-testid="share-event-btn"
              >
                <Share2 className="w-4 h-4 mr-2" />
                Share
              </Button>

              {/* Share Dropdown */}
              {showShareMenu && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-10">
                  <button
                    className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                    onClick={() => handleShare("copy")}
                  >
                    <Copy className="w-4 h-4" />
                    Copy Link
                  </button>
                  <button
                    className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                    onClick={() => handleShare("whatsapp")}
                  >
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                    </svg>
                    WhatsApp
                  </button>
                  {navigator.share && (
                    <button
                      className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                      onClick={() => handleShare("native")}
                    >
                      <Share2 className="w-4 h-4" />
                      More Options...
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Partner Enterprise */}
        {event.partner_enterprise_info && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-6">
            <p className="text-sm text-gray-500 mb-2">In collaboration with</p>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <Building2 className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="font-medium text-gray-900">
                  {event.partner_enterprise_info.display_name}
                </p>
                <p className="text-xs text-gray-500">Enterprise Partner</p>
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Click outside to close share menu */}
      {showShareMenu && (
        <div
          className="fixed inset-0 z-0"
          onClick={() => setShowShareMenu(false)}
        />
      )}

      <WhatsAppSupport />
    </div>
  );
}
